import * as React from 'react';
import { useDispatch } from 'react-redux';

import { alertSuccess } from '../../../../store/reducers/Alert';
import { ContextMenuItem, TreeEditor } from '../../../../components/tree/TreeEditor';
import { DocTemplateFolderTypeVals } from '../../../../helpers/constants/modelValues/DocTemplateFolderTypeVals';
import { EditSectionDialog } from './EditSectionDialog';
import { EditTomDialog } from './EditTomDialog';
import { KAProjectDocFolderActions } from './KAProjectDocFolderActions';
import { NodeInfoBar } from './NodeInfoBar';
import { MenuItemValues } from '../../../../helpers/constants/systemValues/MenuItemValues';
import { ModelAction } from '../../../../types/responses/ModelAction';
import { ProjectDocFolder } from '../../models/ProjectDocFolder';
import { ProjectDocFolderTreeNode } from '../../models/ProjectDocFolderTreeNode';
import { ProjectDocFolderStatuses } from '../../../../helpers/mapping/ProjectDocFolderStatuses';
import { services } from '../../../../services';
import { useBackgroundPromiseWrapper } from '../../../../helpers/hooks/useBackgroundPromiseWrapper';
import { ProjectDocFolderSubTypes } from '../../../../helpers/mapping/ProjectDocFolderSubTypes';
import { ProjectDocFolderSurveyTypes } from '../../../../helpers/mapping/ProjectDocFolderSurveyTypes';
import { ProjectDoc } from '../../models/ProjectDoc';
import { normalizeProjectType, ProjectTypeValues } from '../../../../helpers/mapping/ProjectTypes';
import { CriticalNoteModeEnum, WorkflowCriticalNoteResult } from '../../../notes/models/WorkflowCriticalNoteResult';
import { ProjectDocFlowType } from '../../models/enums/ProjectDocFlowType';
import { ContractorProjectDocActions } from '../../../../helpers/constants/modelActions/ContractorProjectDocActions';
import { ProjectDocTypeVals } from '../../../../helpers/mapping/ProjectDocTypes';
import { AddActDocumentDialog } from './AddActDocumentDialog';

interface KAProjectDocFolderProps {
    treeNodes: ProjectDocFolderTreeNode[];
    projectDocId: number;
    projectDoc?: ProjectDoc;
    docTypeId: number;
    selectedNode?: ProjectDocFolderTreeNode;
    onSelectNode: (node: ProjectDocFolderTreeNode) => void;
    refreshData: () => void;
    onPaperSigningGeneratedChange?: (generated: boolean) => void;
}

export interface KAProjectDocFolderRef {
    loadActions: () => void;
}

export const KAProjectDocFolder = React.forwardRef<KAProjectDocFolderRef, KAProjectDocFolderProps>(
    (
        {
            treeNodes,
            projectDocId,
            projectDoc,
            docTypeId,
            selectedNode,
            onSelectNode,
            refreshData,
            onPaperSigningGeneratedChange
        },
        ref
    ) => {
        const dispatch = useDispatch();
        const [promiseWrapper, isLoading] = useBackgroundPromiseWrapper();
        const [actionsLoaded, setActionsLoaded] = React.useState(false);
        const [actions, setActions] = React.useState<ModelAction[]>([]);
        const [criticalFolderResult, setCriticalFolderResult] = React.useState<WorkflowCriticalNoteResult[]>([]);
        const [editDocumentDialogVisible, setEditDocumentDialogVisible] = React.useState(false);
        const [folderForEditDocument, setFolderForEditDocument] = React.useState<ProjectDocFolder | undefined>();

        const criticalFolderMap = React.useMemo(() => {
            const map = new Map<number, CriticalNoteModeEnum>();
            criticalFolderResult.forEach((x) => {
                if (x.CriticalNoteMode !== 0) map.set(x.NoteObjectId, x.CriticalNoteMode);
            });
            return map;
        }, [criticalFolderResult]);

        const loadActions = React.useCallback(() => {
            if (projectDocId === 0) return;

            promiseWrapper(
                () => services.projectDocService.actions(projectDocId),
                (res: ModelAction[]) => {
                    setActions(res);
                    setActionsLoaded(true);
                }
            );
            // eslint-disable-next-line
        }, [promiseWrapper, projectDocId]);
        // eslint-disable-next-line
        React.useEffect(loadActions, [projectDocId]);

        const paperSigningTabVisible = React.useMemo(() => {
            if (!actionsLoaded) return false;
            if (projectDoc?.DocFlowType !== ProjectDocFlowType.Paper) return false;
            return actions.some((a) => a.Id === ContractorProjectDocActions.PaperSigningGenerated);
        }, [actions, actionsLoaded, projectDoc?.DocFlowType]);

        React.useEffect(() => {
            onPaperSigningGeneratedChange?.(paperSigningTabVisible);
        }, [paperSigningTabVisible]);

        React.useImperativeHandle(ref, () => ({ loadActions }), [loadActions]);

        const loadCriticalFolderIds = React.useCallback(() => {
            if (projectDocId === 0) return;

            promiseWrapper(
                () => services.pDFNoteWorkflowInstanceService.fetchCriticalFolderFlags(projectDocId),
                (res: WorkflowCriticalNoteResult[]) => {
                    setCriticalFolderResult(res);
                }
            );
        }, [projectDocId, promiseWrapper]);

        React.useEffect(loadCriticalFolderIds, [projectDocId]);

        const createNewFolder = React.useCallback(
            (folderTypeId: number, parentId?: number) => {
                return {
                    Id: 0,
                    Title: ``,
                    Cypher: ``,
                    Children: [],
                    ChangeChildren: [],
                    Attachments: [],
                    WorkflowApprovers: [],
                    FolderTypeId: folderTypeId,
                    ParentId: parentId,
                    SubTypeId: ProjectDocFolderSubTypes.Standart,
                    SurveyTypeId: ProjectDocFolderSurveyTypes.Standart,
                    InputControl: false,
                    DisablePrimaryEmailNotification: false,
                    ProjectDocId: projectDocId,
                    Position: 0,
                    ProjectDocFolderCUsers: []
                };
            },
            [projectDocId]
        );

        const projectType = normalizeProjectType(projectDoc?.Project?.Directorate);

        const isExecutiveGeneral =
            projectDoc?.DocTypeId === ProjectDocTypeVals.Executive &&
            projectType === ProjectTypeValues.CapitalConstruction;

        const [editSectionDialogVisible, setEditSectionDialogVisible] = React.useState<boolean>(false);
        const [editTomDialogVisible, setEditTomDialogVisible] = React.useState<boolean>(false);
        const [folderEdit, setFolderEdit] = React.useState<ProjectDocFolder>(
            createNewFolder(DocTemplateFolderTypeVals.Section)
        );

        const onSave = (folder: ProjectDocFolder) => {
            promiseWrapper(
                () => services.projectDocFolderService.save([folder]),
                (res) => {
                    dispatch(alertSuccess());
                    refreshData();
                    loadActions();
                    setEditSectionDialogVisible(false);
                    setEditTomDialogVisible(false);
                }
            );
        };

        const isExecutiveReconstruction =
            projectDoc?.DocTypeId === ProjectDocTypeVals.Executive && projectType === ProjectTypeValues.Reconstruction;

        const onCancel = () => {
            setEditSectionDialogVisible(false);
            setEditTomDialogVisible(false);
        };

        const onEditDocumentSave = (folder: ProjectDocFolder) => {
            onSave(folder);
            setEditDocumentDialogVisible(false);
            setFolderForEditDocument(undefined);
        };

        const leafMenuItems: ContextMenuItem[] = [
            {
                disabled: isLoading,
                type: MenuItemValues.Edit,
                label: 'Изменить',
                icon: 'fa fa-pencil',
                command: () => setEditTomDialogVisible(true)
            }
        ];

        const nodeMenuItems: ContextMenuItem[] = [];
        const addDocumentSlotLabel =
            projectDoc?.DocTypeId === ProjectDocTypeVals.Executive &&
            projectType === ProjectTypeValues.CapitalConstruction
                ? 'Добавить документ'
                : 'Добавить том';

        const workTypeMenuItems: ContextMenuItem[] = [
            {
                disabled: isLoading,
                type: MenuItemValues.AddTom,
                label: addDocumentSlotLabel,
                icon: 'fa fa-file',
                command: () => {
                    setFolderEdit(createNewFolder(DocTemplateFolderTypeVals.Tom, folderEdit.Id));
                    setEditTomDialogVisible(true);
                }
            }
        ];

        const actMenuItems: ContextMenuItem[] = [
            {
                disabled: isLoading,
                type: MenuItemValues.Edit,
                label: 'Изменить',
                icon: 'fa fa-pencil',
                command: () => setEditTomDialogVisible(true)
            }
        ];

        const actDocumentSectionMenuItems: ContextMenuItem[] = [];

        const actSectionMenuItems: ContextMenuItem[] = [];

        const actDocumentMenuItems: ContextMenuItem[] = [
            {
                disabled: isLoading,
                type: MenuItemValues.Edit,
                label: 'Изменить',
                icon: 'fa fa-pencil',
                command: () => {
                    if (isExecutiveGeneral || isExecutiveReconstruction) {
                        setFolderForEditDocument(folderEdit);
                        setEditDocumentDialogVisible(true);
                    } else {
                        setEditTomDialogVisible(true);
                    }
                }
            }
        ];

        const canEditNode = React.useCallback(
            (node: ProjectDocFolderTreeNode) => {
                if (paperSigningTabVisible) return false;

                if (node.FolderTypeId === DocTemplateFolderTypeVals.Section) {
                    if (
                        node.StatusId === ProjectDocFolderStatuses.NoData ||
                        node.StatusId === ProjectDocFolderStatuses.Recalled ||
                        node.StatusId === ProjectDocFolderStatuses.OnContractorDevelopment
                    ) {
                        if (node.children.length) {
                            const child = node.children[0] as ProjectDocFolderTreeNode;
                            return child.FolderTypeId === DocTemplateFolderTypeVals.Tom;
                        }
                        return true;
                    }
                    return false;
                }

                if (
                    node.FolderTypeId === DocTemplateFolderTypeVals.Section &&
                    node.ParentId == null &&
                    node.data?.StructureType != null
                ) {
                    return true;
                }
                const actDocumentMenuItems: ContextMenuItem[] = [
                    {
                        disabled: isLoading,
                        type: MenuItemValues.Edit,
                        label: 'Изменить',
                        icon: 'fa fa-pencil',
                        command: () => {
                            if (isExecutiveReconstruction) {
                                setFolderForEditDocument(folderEdit);
                                setEditDocumentDialogVisible(true);
                            } else {
                                setEditTomDialogVisible(true);
                            }
                        }
                    }
                ];

                if (
                    node.FolderTypeId === DocTemplateFolderTypeVals.Act &&
                    (node.StatusId === ProjectDocFolderStatuses.NoData ||
                        node.StatusId === ProjectDocFolderStatuses.Recalled ||
                        node.StatusId === ProjectDocFolderStatuses.OnContractorDevelopment)
                ) {
                    return true;
                }

                if (
                    node.FolderTypeId === DocTemplateFolderTypeVals.ActDocument &&
                    (node.StatusId === ProjectDocFolderStatuses.NoData ||
                        node.StatusId === ProjectDocFolderStatuses.Recalled ||
                        node.StatusId === ProjectDocFolderStatuses.OnContractorDevelopment)
                ) {
                    return true;
                }

                return (
                    node.StatusId === ProjectDocFolderStatuses.NoData ||
                    node.StatusId === ProjectDocFolderStatuses.Recalled
                );
            },
            [paperSigningTabVisible]
        );

        const onPositionsChanged = (ids: number[]) => {
            promiseWrapper(
                () => services.projectDocFolderService.reorder(ids),
                (res) => {
                    dispatch(alertSuccess());
                    refreshData();
                }
            );
        };

        return (
            <React.Fragment>
                <div className="pd-structure-actions">
                    <KAProjectDocFolderActions
                        projectDocId={projectDocId}
                        treeNodes={treeNodes}
                        onProjectDocStatusUpdated={refreshData}
                        actions={actions}
                        loadActions={loadActions}
                        onPaperSigningGenerated={loadActions}
                    />
                </div>
                <TreeEditor
                    projectDoc={projectDoc}
                    treeNodes={treeNodes}
                    leafMenuItems={leafMenuItems}
                    nodeMenuItems={nodeMenuItems}
                    actMenuItems={actMenuItems}
                    actDocumentMenuItems={actDocumentMenuItems}
                    actDocumentSectionMenuItems={actDocumentSectionMenuItems}
                    actSectionMenuItems={actSectionMenuItems}
                    workTypeMenuItems={workTypeMenuItems}
                    onMenu={(node) => setFolderEdit((node as ProjectDocFolderTreeNode).data)}
                    afterNodeValue={(node) => <NodeInfoBar node={node as ProjectDocFolderTreeNode} />}
                    onPositionsChanged={onPositionsChanged}
                    canEditNode={(e) => canEditNode(e as ProjectDocFolderTreeNode)}
                    enableScroll={true}
                    selectionMode="single"
                    selectionKeys={selectedNode?.key as any}
                    onSelect={(e) => onSelectNode(e.node as ProjectDocFolderTreeNode)}
                    criticalFolderMap={criticalFolderMap}
                    criticalBeforeFileIcon
                    cypherMatchesProgress
                />
                <EditSectionDialog
                    visible={editSectionDialogVisible}
                    folder={folderEdit}
                    onSave={onSave}
                    onCancel={onCancel}
                />
                <EditTomDialog
                    visible={editTomDialogVisible}
                    folder={folderEdit}
                    docTypeId={docTypeId}
                    onSave={onSave}
                    onCancel={onCancel}
                />
                {folderForEditDocument && (
                    <AddActDocumentDialog
                        visible={editDocumentDialogVisible}
                        folder={folderForEditDocument}
                        editFolder={folderForEditDocument}
                        docTypeId={docTypeId}
                        onSave={() => {}}
                        onSaveEdit={onEditDocumentSave}
                        onCancel={() => {
                            setEditDocumentDialogVisible(false);
                            setFolderForEditDocument(undefined);
                        }}
                    />
                )}
            </React.Fragment>
        );
    }
);
